import { useEffect, useRef } from 'react';
import { setDefaultOptions } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import {
  buildHoursArray,
  buildMinutesArray,
  renderDateWithTime,
  renderTextDate,
} from '@/utils/time';
import Popover from '@mui/material/Popover';
import Paper from '@mui/material/Paper';
import TimePicker from '@/components/TimePicker';
import { EventIcon } from '@/icons';
import { useToggle } from '@/hooks/useToggle';
import type { WeekStartsOn } from '@/types/dates';
import { classes, styles } from './utils';

export type DateTimeInputProps<N extends string> = {
  className?: string;
  disabled?: boolean;
  disablePast?: boolean;
  showTime?: boolean;
  closeOnSelect?: boolean;
  value?: Date;
  dateFormat?: string;
  weekStartsOn?: WeekStartsOn;
  name: N;
  label: string;
  onChange: (value: Record<N, Date>) => void;
};

export default function DateTimeInput<N extends string>({
  className,
  disabled,
  disablePast = true,
  showTime = true,
  closeOnSelect = false,
  weekStartsOn = 0,
  dateFormat,
  value,
  name,
  label,
  onChange,
}: DateTimeInputProps<N>) {
  const inputRef = useRef(null);
  const [isOpen, toggleOpen] = useToggle(false);

  setDefaultOptions({ weekStartsOn });

  useEffect(() => {
    // Default to 24 hours from now
    if (!value) {
      let time = new Date();
      time.setDate(time.getDate() + 1);
      // Ignore seconds/ms
      time.setSeconds(0, 0);
      // Round to the nearest increment of 5
      if (showTime) {
        time.setMinutes(Math.round(time.getMinutes() / 5) * 5);
      }
      onChange({ [name]: time } as Record<N, Date>);
    }
  }, [value]);

  const handleSelectDate = (val: Date) => {
    let time = value ? new Date(value.toISOString()) : new Date();
    time.setFullYear(val.getFullYear(), val.getMonth(), val.getDate());
    onChange({ [name]: time } as Record<N, Date>);
    if (closeOnSelect) toggleOpen(false);
  };

  const handleSelectTime = (update: { hours?: number; minutes?: number }) => {
    let time = value ? new Date(value.toISOString()) : new Date();
    if (update.hasOwnProperty('hours')) time.setHours(update.hours!);
    if (update.hasOwnProperty('minutes')) time.setMinutes(update.minutes!);
    onChange({ [name]: time } as Record<N, Date>);
    if (closeOnSelect) toggleOpen(false);
  };

  const renderValue = () => {
    if (!value) return null;
    return showTime
      ? renderDateWithTime(value.toISOString(), dateFormat)
      : renderTextDate(value.toISOString(), dateFormat);
  };

  return (
    <FormControl
      disabled={disabled}
      className={className}
      size="small"
      variant="outlined">
      <InputLabel htmlFor={name}>{label}</InputLabel>
      <OutlinedInput
        ref={inputRef}
        id={name}
        label={label}
        value={renderValue()}
        onClick={() => toggleOpen()}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              edge="end"
              aria-label="toggle calendar visibility"
              sx={styles.iconButton}>
              <EventIcon />
            </IconButton>
          </InputAdornment>
        }
      />
      <Popover
        open={isOpen}
        anchorEl={inputRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={() => toggleOpen(false)}>
        <Paper className={classes.paper}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              showDaysOutsideCurrentMonth
              disablePast={disablePast}
              sx={styles.calendar}
              value={value}
              onChange={handleSelectDate}
            />
          </LocalizationProvider>
          {showTime && (
            <TimePicker
              hours={value?.getHours()}
              minutes={value?.getMinutes()}
              hoursOptions={buildHoursArray(true)}
              minutesOptions={buildMinutesArray(5)}
              onChange={handleSelectTime}
            />
          )}
        </Paper>
      </Popover>
    </FormControl>
  );
}
